import { useEffect, useMemo, useState } from 'react';
import type { Map as MlMap } from 'maplibre-gl';
import { FloatPill, Handle, MapSheet, Segments, SheetFoot, Tag, VehicleImage } from '../components/ui';
import { Icon } from '../components/Icon';
import { MapMarker, useFit } from '../components/MapView';
import { useApp } from '../lib/app';
import { clock, km, rupees } from '../lib/format';
import { useT } from '../lib/i18n';
import { matchingView } from '../lib/matching';
import { haversine } from '../lib/geo';
import { nearestLandmark, shortName } from '../lib/places';
import type { Nearby, Ride } from '../lib/types';
import { meta, words } from '../lib/vehicles';
import { MapStrip, VehicleDisc, YouDot } from './mapBits';

/** The radar's outer ring (px); nearby drivers are framed inside it. */
const RING = 150;

/** Zoom at which `meters` from the pickup is `px` pixels on screen (Web Mercator, MapLibre 512 px tiles). */
function zoomFor(meters: number, px: number, lat: number) {
  return Math.log2((78271.52 * Math.cos((lat * Math.PI) / 180) * px) / Math.max(meters, 1));
}

export function Finding({ ride, onCancel, cancelling, map }: { ride: Ride; onCancel: () => void; cancelling: boolean; map: MlMap | null }) {
  const { api } = useApp();
  const t = useT();
  const [nearby, setNearby] = useState<Nearby | null>(null);
  useEffect(() => {
    let live = true;
    const load = () => api.nearby(ride.pickup).then((n) => live && setNearby(n), () => undefined);
    void load();
    const tm = window.setInterval(load, 8000);
    return () => {
      live = false;
      window.clearInterval(tm);
    };
  }, [api, ride.pickup]);

  const m = { ...meta(ride.vehicleType), ...words(t, ride.vehicleType) };
  const mv = matchingView(ride.matching, nearby, ride.vehicleType);
  const { asked, looking, offers, segs } = mv;
  const area = shortName(ride.pickup.address);
  const sameType = useMemo(
    () => (nearby?.points ?? []).filter((p) => p.vehicleType === ride.vehicleType).sort((a, b) => haversine(ride.pickup, a) - haversine(ride.pickup, b)).slice(0, 6),
    [nearby, ride.pickup, ride.vehicleType],
  );
  const isLooking = (p: { lat: number; lng: number }) => offers.some((o) => o.near && haversine(o.near, p) < 30);
  const label = (o: (typeof offers)[number]) => {
    const near = o.near ? nearestLandmark(o.near, 700) : null;
    return near ? t(near.kind === 'spot' ? 'find.near' : 'find.in', { type: m.label, place: near.name }) : t('find.nearby', { type: m.label });
  };

  // frame the pickup in the middle of the visible map, zoomed so the nearest drivers sit inside the radar
  const far = sameType.length ? haversine(ride.pickup, sameType[sameType.length - 1]) : 0;
  const zoom = far ? Math.min(16, Math.max(13.5, zoomFor(far, RING - 20, ride.pickup.lat))) : 15.3;
  useFit(map, { points: [ride.pickup], padding: { top: 160, bottom: 0, left: 0, right: 0 }, maxZoom: Math.round(zoom * 10) / 10 });

  return (
    <>
      {sameType.map((p, i) => (
        <VehicleDisc key={`${i}`} at={p} type={p.vehicleType} size={isLooking(p) ? 34 : 30} ring={isLooking(p) ? 2 : 0} faded={!isLooking(p)} glide={1200} />
      ))}
      <MapMarker at={ride.pickup} z={2}>
        <svg width="320" height="320" viewBox="0 0 320 320" aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
          <circle cx="160" cy="160" r="156" fill="none" style={{ stroke: 'var(--ac)' }} strokeWidth="1" opacity=".22" />
          <circle cx="160" cy="160" r="106" fill="none" style={{ stroke: 'var(--ac)' }} strokeWidth="1" opacity=".38" />
          <circle cx="160" cy="160" r="56" style={{ fill: 'var(--acSoft)', stroke: 'var(--ac)' }} strokeWidth="1.2" strokeOpacity=".6" />
          {/* the search going out: a ring that grows from the pickup to the outer ring, again and again */}
          <circle cx="160" cy="160" r="156" fill="none" style={{ stroke: 'var(--ac)', transformOrigin: '160px 160px', animation: 'zr-ripple 2.8s cubic-bezier(.2,.6,.3,1) infinite' }} strokeWidth="1.5" />
          <circle cx="160" cy="160" r="156" fill="none" style={{ stroke: 'var(--ac)', transformOrigin: '160px 160px', animation: 'zr-ripple 2.8s cubic-bezier(.2,.6,.3,1) 1.4s infinite' }} strokeWidth="1.5" />
        </svg>
      </MapMarker>
      <YouDot at={ride.pickup} />
      <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'var(--dim)', pointerEvents: 'none', zIndex: 1 }} />

      <FloatPill style={{ position: 'absolute', left: 'calc(var(--map-l) + (100% - var(--map-l)) / 2)', top: 'calc(var(--sat) + 14px)', transform: 'translateX(-50%)', gap: 8, maxWidth: 'calc(100% - var(--map-l) - 40px)', zIndex: 5 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{area}</span>
        <Icon name="arrow" size={14} sw={2.4} style={{ color: 'var(--ink3)', flex: 'none' }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{shortName(ride.drop.address)}</span>
        <span style={{ color: 'var(--ink3)' }}>·</span> {rupees(ride.farePaise)}
      </FloatPill>

      <MapSheet label={t('find.sheet', { noun: m.noun })} art={<MapStrip />}>
        <div className="zr-swap" aria-live="polite" style={{ display: 'flex', flexDirection: 'column' }}>
          <Handle />
          <h2 style={{ margin: '20px 0 0', fontSize: 24, lineHeight: '30px', fontWeight: 700, letterSpacing: '-0.02em' }}>{t('find.title', { type: m.label, noun: m.noun })}</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)' }}>{mv.empty ? t('find.empty', { plural: m.plural, time: clock(ride.matchDeadline) }) : t('find.asking', { plural: m.plural, area })}</p>

          <div style={{ marginTop: 18 }}>
            <Segments total={segs} asked={asked} looking={looking} label={t('find.bar', { asked, plural: m.plural, looking })} testId="matching" data={{ asked, looking }} />
          </div>
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13, lineHeight: '18px', fontWeight: 700 }}>
            <span key={`a${asked}`} className="zr-tick" style={{ color: 'var(--ink2)' }}>
              {asked ? t.n('find.asked', asked, { noun: m.noun, plural: m.plural }) : mv.online ? t.n('find.nearbyCount', mv.online, { noun: m.noun, plural: m.plural }) : t('find.noneYet', { plural: m.plural })}
            </span>
            <span key={`l${looking}`} className="zr-tick" style={{ color: 'var(--acText)' }}>
              {looking ? t('find.lookingN', { n: looking }) : asked ? t('find.next') : t('find.still')}
            </span>
          </div>

          <div className="zr-stagger" style={{ marginTop: 14, display: 'flex', flexDirection: 'column' }}>
            {offers.slice(0, 2).map((o, i, arr) => (
              <div key={i} style={{ height: 56, display: 'flex', alignItems: 'center', gap: 12, boxShadow: i < arr.length - 1 ? 'inset 0 -1px 0 var(--line)' : undefined }}>
                <span aria-hidden="true" style={{ flex: 'none', width: 40, height: 40, borderRadius: 13, background: 'var(--s1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <VehicleImage type={ride.vehicleType} width={30} height={18} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="zr-ellipsis" style={{ display: 'block', fontSize: 15, lineHeight: '20px', fontWeight: 700 }}>{label(o)}</span>
                  <span style={{ display: 'block', fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>{t('find.away', { d: km(o.distanceMeters) })}</span>
                </span>
                <Tag style={{ animation: 'zr-breathe 1.8s ease-in-out infinite' }}>{t('find.looking')}</Tag>
              </div>
            ))}
          </div>
        </div>

        <SheetFoot style={{ marginTop: 'auto', paddingTop: 'calc(32px * var(--vg))' }}>
          <button type="button" disabled={cancelling} onClick={onCancel} aria-haspopup="dialog" className="zr-btn zr-btn--tonal">
            {cancelling ? t('common.cancelling') : t('find.cancel')} <span style={{ fontWeight: 500, color: 'var(--ink2)' }}>· {t('common.free')}</span>
          </button>
        </SheetFoot>
      </MapSheet>
    </>
  );
}
